import { useContext, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { Button, Stack } from '@chakra-ui/react';

import { UseOpenCloseReturn } from '~/core/hooks/useOpenClose';
import {
  useEquifaxPpsrRegistrationForm,
  useRunEquifaxPpsrSerialNumberSearchRequest,
} from '~/core/shared/api/plugin';
import { Dialog, FormField, Input, Select } from '~/core/shared/components';
import {
  SERIAL_NUMBER_TYPE_LABELS,
  SerialNumberType,
} from '~/core/shared/constants/ppsr';
import { useFormResolver } from '~/core/shared/hooks';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import {
  ApplicationContext,
  DocumentDetailsViewActionContext,
} from '../../Layout.component';

interface EquifaxPpsrAsset {
  id: string;
  label?: string;
  serialNumber?: string;
  serialNumberType?: string;
}

const serialNumberTypeOptions = Object.values(SerialNumberType).map(
  (value) => ({
    label: SERIAL_NUMBER_TYPE_LABELS[value],
    value,
  }),
);

type FormValues = {
  assetId: string;
  serialNumberType: string;
  serialNumber: string;
};

/**
 * @TODO Prevent this modal from being eager loaded on every application load.
 */
const validationSchema = yup.object({
  assetId: yup.string().trim().required(),
  serialNumberType: yup.string().trim().required(),
  serialNumber: yup.string().trim().required(),
});

export const EquifaxPPSRModal = () => {
  const { applicationUuid, setLastApplicationUpdate, actionContexts } =
    useContext(ApplicationContext);

  const {
    action: { key },
  } = useContext(DocumentDetailsViewActionContext);

  const equifaxPPSRModalApi: UseOpenCloseReturn = actionContexts[key].modal;

  const [assetOptions, setAssetOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [assetsMap, setAssetsMap] = useState<Record<string, EquifaxPpsrAsset>>(
    {},
  );
  // TODO: @haxxxton update this to use form blocks
  const resolver = useFormResolver<FormValues>(validationSchema);
  const form = useForm<FormValues>({
    resolver,
    defaultValues: {
      assetId: '',
      serialNumberType: '',
      serialNumber: '',
    },
  });

  const { formData } = useEquifaxPpsrRegistrationForm(applicationUuid);
  const { runEquifaxPpsrSerialNumberSearchRequest, isPending } =
    useRunEquifaxPpsrSerialNumberSearchRequest();

  useEffect(() => {
    if (formData) {
      const newAssetsMap: Record<string, EquifaxPpsrAsset> = {};
      const transformedOptions: Array<{ label: string; value: string }> = [];

      const data = formData as any;

      if (data.assets?.length) {
        (data.assets as EquifaxPpsrAsset[]).forEach((asset) => {
          transformedOptions.push({
            label: `${asset.label}`,
            value: asset.id,
          });
          newAssetsMap[asset.id] = asset;
        });
      }

      setAssetOptions(transformedOptions);
      setAssetsMap(newAssetsMap);
    }
  }, [formData]);

  useEffect(() => {
    if (equifaxPPSRModalApi.open) {
      form.reset({
        assetId: '',
        serialNumberType: '',
        serialNumber: '',
      });
    }
  }, [equifaxPPSRModalApi.open]);

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'assetId' && value.assetId) {
        const asset = assetsMap[value.assetId];

        if (asset) {
          form.setValue('serialNumber', asset.serialNumber || '');
          form.setValue('serialNumberType', SerialNumberType.VIN);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [form, assetsMap]);

  const onSubmit = useEventHandler(
    async (values: FormValues): Promise<void> => {
      if (!values.serialNumber || !values.serialNumberType) {
        return;
      }

      try {
        await runEquifaxPpsrSerialNumberSearchRequest({
          params: {
            uuid: applicationUuid,
            serialNumber: values.serialNumber,
            serialNumberType: values.serialNumberType,
          },
        });
        setLastApplicationUpdate(new Date());
        equifaxPPSRModalApi.onClose();
      } catch {
        // Error is handled in useRunEquifaxPpsrSerialNumberSearchRequest
      }
    },
  );

  return (
    <Dialog
      header="Equifax - PPSR Serial Search"
      onOpenChange={equifaxPPSRModalApi.onOpenChange}
      open={equifaxPPSRModalApi.open}
      size="lg"
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Stack gap={4} p={6}>
            <FormField
              component={Select}
              label="Asset"
              name="assetId"
              options={assetOptions}
              placeholder="Select Asset"
              required
            />

            <FormField
              component={Select}
              label="Serial Number Type"
              name="serialNumberType"
              options={serialNumberTypeOptions}
              placeholder="Select Serial Number Type"
              required
            />

            <FormField
              component={Input}
              label="Serial Number"
              name="serialNumber"
              placeholder="e.g. MPATFR85JHT000404"
              required
            />

            <Stack direction="row" justifyContent="end">
              <Button
                colorPalette="gray"
                fontSize="md"
                fontWeight="normal"
                onClick={equifaxPPSRModalApi.onClose}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                colorPalette="primary"
                disabled={
                  !form.watch('serialNumberType') || !form.watch('serialNumber')
                }
                fontSize="md"
                fontWeight="bold"
                loading={isPending}
                type="submit"
                variant="solid"
              >
                Search
              </Button>
            </Stack>
          </Stack>
        </form>
      </FormProvider>
    </Dialog>
  );
};
